import cx from 'classnames';
import { useRef, useState } from 'react';

import { Icon, useClickOutside } from '@sb/design-system';
import type { IconKind } from '@sb/design-system';

import { ChatMenuItem, type ChatMenuItemDef } from './ChatMenuItem';

/**
 * @deprecated Prefer `ChatMenuItemDef`. Kept as an alias for the
 * existing public prop surface so consumers of `ChatHeader` don't
 * need to update their imports.
 */
export type ChatHeaderMenuItem = ChatMenuItemDef;

export interface ChatHeaderLeftAction {
  iconKind: IconKind;
  ariaLabel: string;
  onClick: () => void;
}

interface ChatHeaderProps {
  title?: string;
  /**
   * Optional kebab menu. When supplied, the header renders the kebab
   * button and an absolutely-positioned popover that lists these items
   * on click. Click-outside the popover dismisses it. When omitted,
   * the kebab is hidden entirely.
   */
  menuItems?: ChatHeaderMenuItem[];
  /**
   * Optional icon button in the left column (e.g. a back arrow when
   * the panel is showing chat history). Replaces the default empty
   * spacer.
   */
  leftAction?: ChatHeaderLeftAction;
}

export const DEFAULT_CHAT_TITLE = 'Routine Agent';

/**
 * Header strip at the top of the chat card. Three-column grid (left
 * action / centered title / right kebab) plus an absolutely-positioned
 * popover that opens off the kebab when `menuItems` is supplied.
 */
const ICON_BUTTON_CLASS = cx(
  'tw-flex',
  'tw-items-center',
  'tw-justify-center',
  'tw-w-32',
  'tw-h-32',
  'tw-bg-transparent',
  'tw-border-none',
  'tw-cursor-pointer',
  'tw-p-0',
);

// `borderRadius: 8px` and the hover-background transition aren't in
// the design-system scale (radius jumps 6 → 10; no transition token
// for this exact curve), so they stay inline.
const ICON_BUTTON_INLINE_STYLE: React.CSSProperties = {
  borderRadius: 8,
  transition: 'background 0.12s ease',
};

export function ChatHeader({
  title = DEFAULT_CHAT_TITLE,
  menuItems,
  leftAction,
}: ChatHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useClickOutside([menuContainerRef], () => {
    if (isMenuOpen) setIsMenuOpen(false);
  });

  return (
    <div
      className={cx(
        'tw-grid',
        'tw-items-center',
        'tw-pt-12',
        'tw-px-16',
        'tw-pb-10',
        'tw-shrink-0',
        'tw-relative',
      )}
      style={{
        // Custom three-column grid, the specific header drop-shadow,
        // and z-index don't map to design-system tokens, so they stay
        // inline; the surface uses the themeable
        // `--surface-primary-elevated` token.
        gridTemplateColumns: '32px 1fr 32px',
        background: 'var(--surface-primary-elevated)',
        boxShadow: '0 4px 10px -6px rgba(0,0,0,0.08)',
        zIndex: 2,
      }}
    >
      {/* Left column — back arrow or other icon-button, otherwise an
          empty spacer so the centered title stays optically centered. */}
      <div className="tw-flex tw-justify-start">
        {leftAction ? (
          <button
            type="button"
            onClick={leftAction.onClick}
            aria-label={leftAction.ariaLabel}
            className={ICON_BUTTON_CLASS}
            style={ICON_BUTTON_INLINE_STYLE}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--fill-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <Icon kind={leftAction.iconKind} className="tw-icon-22" />
          </button>
        ) : null}
      </div>

      {/* Center column — title. */}
      <div className="tw-flex tw-justify-center tw-items-center">
        <span
          className="tw-select-none"
          style={{
            // The design-system typography scale doesn't include 16px
            // font size, 600 weight, or this letter-spacing value, so
            // the title text styling stays inline.
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--label-primary)',
            letterSpacing: -0.3,
          }}
        >
          {title}
        </span>
      </div>

      {/* Right column — kebab + popover (when menuItems provided). */}
      <div
        ref={menuContainerRef}
        className="tw-relative tw-flex tw-justify-end"
      >
        {menuItems && menuItems.length > 0 ? (
          <>
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label="Chat menu"
              aria-expanded={isMenuOpen}
              className={ICON_BUTTON_CLASS}
              style={ICON_BUTTON_INLINE_STYLE}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--fill-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              <Icon kind="ellipsisCircle" className="tw-icon-22" />
            </button>
            {isMenuOpen && (
              <div
                role="menu"
                aria-label="Chat actions"
                className={cx(
                  'tw-absolute',
                  'tw-right-0',
                  'tw-flex',
                  'tw-flex-col',
                  'tw-gap-2',
                  'tw-p-4',
                )}
                style={{
                  // Anchor 36px below the 32px kebab + 4px gap, with
                  // a 150px min-width so single-word items still feel
                  // like a popover not a chip. The radius / shadow /
                  // border / z-index values don't map to design-system
                  // tokens (the radius scale jumps 10→14, no 150 spacing
                  // token), so they stay inline.
                  top: 36,
                  minWidth: 150,
                  background: 'var(--surface-primary-elevated)',
                  borderRadius: 12,
                  boxShadow:
                    '0 12px 32px -8px rgba(0,0,0,0.18), 0 4px 12px -4px rgba(0,0,0,0.10)',
                  border: '1px solid var(--divider-primary)',
                  zIndex: 10,
                }}
              >
                {menuItems.map((item) => (
                  <ChatMenuItem
                    key={item.label}
                    item={item}
                    onAfterClick={() => setIsMenuOpen(false)}
                  />
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
